import axios from "axios"
import { env } from "../Utils/Environment"
import { AccountsGetModel, UploadLogoPostModel } from "../models/Accounts"
import { UserGetModel } from "../models/Users"
import { AuthPostModel, LogoutModel } from "../models/Auth"
import { getBaseToken, getToken, userLogout } from "../Utils/Auth"
import { GetProductResponse } from "../types/product"
import { GetAllCategoriesResponse } from "../types/category"
import { GetAllSchedulesResponse } from "../types/schedule"
import { GetTransactionResponse } from "../types/transaction"
import { GetReferralResponse } from "../types/referral"
import { GetDeliveryTypeResponse } from "../types/delivery"
import { GetAlertSettingResponse } from "../types/alert"
import { GetLocationResponse } from "../types/location"
import { PostProductResponse } from "../types/store"
import { StoreProfileResponse, StoreProfileUpdateBody } from "../types/store"
import { GetOrderStatusesResponse, GetStoreOrderDetailResponse, GetStoreOrdersResponse, StoreOrdersParams, UpdateOrderStatusResponse } from "../types/order"
import { DashboardRange, GetStoreDashboardResponse } from "../types/dashboard"
import { ProductFormResponseData, StoreTopping, StoreToppingGroup } from "../types/product-form"
import { ComboFormResponseData, ComboListItem, Paginated } from "../types/combo"
import { AdsPricing, AudienceSegment, DiscountItem, DiscountType, StoreAd } from "../types/marketing"
import { UserSession } from "../types/session"
import { AssignmentCandidate, DeliveryCompany, FleetCourier, FleetDelivery, FleetDeliveryDetail, FleetLive } from "../types/fleet"
import { Pagination } from "../types/order"
import { DeliveryRoute, FleetCourierRoute } from "../types/deliveryRoute"

// Respuesta estándar del backend: [{ message, status, code, code_app, data }][0]
interface ApiResponse<T> {
    message: string;
    status: number;
    code: string;
    code_app: string;
    data: T;
}

const API = env('API_ENDPOINT')
// const API = 'https://sandbox.domicilapp.com/v1/'

const getHeadersAuth = (content_type?: string) => {
    const headers = {
        'Content-Type': content_type ?? 'application/json',
        'Device-Id': localStorage.getItem('device_id'),
        'Authorization': 'Bearer ' + getToken(),
    }
    return headers;
}

const getHeadersAdminAuth = () => {
    const headers = {
        'Content-Type': 'application/json',
        'Device-Id': localStorage.getItem('device_id'),
        'Authorization': 'Bearer ' + getBaseToken(),
    }
    return headers;
}

export const signUp = async (body: Object) => {
    try {
        const response = await axios.post(API + 'user', body, { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        if (error.response && error.response.status === 400) {
            return error.response.data[0];
        } else {
            throw error;
        }
    }
}

export const sendCode = async (body: Object) => {
    try {
        const response = await axios.post(API + 'code/send', body, { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        if (error.response && error.response.status === 400) {
            return error.response.data[0];
        } else {
            throw error;
        }
    }
}

export const verificationCode = async (body: Object) => {
    try {
        const response = await axios.post(API + 'code/verification', body, { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        if (error.response && error.response.status === 400) {
            return error.response.data[0];
        } else {
            throw error;
        }
    }
}

export const login = async (body: Object): Promise<AuthPostModel> => {
    try {
        const response = await axios.post(API + 'user/login', body, { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        // return processErrorResponse(error)
        if (error.response && (error.response.status === 400 || error.response.status === 401 || error.response.status === 500)) {
            return error.response.data[0];
        } else {
            throw error;
        }
    }
}

export const logout = async (): Promise<LogoutModel> => {
    try {
        const response = await axios.post(API + 'user/logout', {}, { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        // return processErrorResponse(error)
        if (error.response && (error.response.status === 400 || error.response.status === 401 || error.response.status === 500)) {
            console.log("Entra a 148");
            return error.response.data[0];
        } else {
            console.log("Entra a 151");
            throw error;
        }
    }
}

export const uploadLogo = async (id: number, logo: File): Promise<UploadLogoPostModel> => {
    try {
        const formData = new FormData();
        formData.append('logo', logo);
        const response = await axios.post(API + 'store/' + id + '/logo', formData, { headers: getHeadersAuth('multipart/form-data') });
        return response.data[0];
    } catch (error: any) {
        // return processErrorResponse(error)
        if (error.response && (error.response.status === 400 || error.response.status === 401 || error.response.status === 500)) {
            return error.response.data[0];
        } else {
            throw error;
        }
    }
}

export const refreshToken = async () => {
    try {
        const response = await axios.post(API + 'user/token/refresh', {}, { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

export const refreshBaseToken = async () => {
    try {
        var headers = getHeadersAuth();
        headers['Authorization'] = getBaseToken()
        const response = await axios.post(API + 'user/token/refresh', {}, { headers: headers });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

export const getStoresByUser = async (): Promise<AccountsGetModel> => {
    try {
        const response = await axios.get(API + 'stores/user', { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

export const getProductsByStore = async (storeId: string, current_page: number = 1): Promise<GetProductResponse> => {
    try {
        const response = await axios.get(API + 'business/store/' + storeId + "/products?per_page=1000", { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

export const addProductToStore = async (storeId: string, body: Object): Promise<GetProductResponse> => {
    try {
        const response = await axios.post(API + 'business/store/' + storeId + "/product", body, { headers: getHeadersAuth('multipart/form-data') });
        // 'Content-Type': 'multipart/form-data',
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

// Producto completo en el formato del wizard, para editarlo
export const getProductForm = async (storeId: string, productId: string): Promise<ApiResponse<ProductFormResponseData>> => {
    try {
        const response = await axios.get(API + 'business/store/' + storeId + '/product/' + productId, { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

// Mismo FormData que addProductToStore (POST: PHP no lee multipart en PUT)
export const updateProductInStore = async (storeId: string, productId: string, body: FormData): Promise<GetProductResponse> => {
    try {
        const response = await axios.post(API + 'business/store/' + storeId + '/product/' + productId, body, { headers: getHeadersAuth('multipart/form-data') });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

// ===================== COMBOS =====================

export const getCombosByStore = async (storeId: string): Promise<ApiResponse<Paginated<ComboListItem>>> => {
    try {
        const response = await axios.get(API + 'business/store/' + storeId + '/combos?per_page=1000', { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

export const getComboForm = async (storeId: string, comboId: string): Promise<ApiResponse<ComboFormResponseData>> => {
    try {
        const response = await axios.get(API + 'business/store/' + storeId + '/combo/' + comboId, { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

// Crear (sin comboId) o actualizar. FormData: data (JSON), images[], images_meta[]
export const saveCombo = async (storeId: string, body: FormData, comboId?: string): Promise<ApiResponse<any>> => {
    try {
        const url = API + 'business/store/' + storeId + '/combo' + (comboId ? '/' + comboId : '');
        const response = await axios.post(url, body, { headers: getHeadersAuth('multipart/form-data') });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

// ===================== MARKETING: DESCUENTOS Y PUBLICIDAD =====================

// type: offers | promotions | coupons
export const getDiscounts = async (storeId: string, type: DiscountType): Promise<ApiResponse<Paginated<DiscountItem>>> => {
    try {
        const response = await axios.get(API + 'business/store/' + storeId + '/marketing/' + type + '?per_page=1000', { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

export const saveDiscount = async (storeId: string, type: DiscountType, body: Object, itemId?: number): Promise<ApiResponse<any>> => {
    try {
        const url = API + 'business/store/' + storeId + '/marketing/' + type + (itemId ? '/' + itemId : '');
        const response = await axios.post(url, body, { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

// Eventos: FormData con data (JSON) e image
export const saveEvent = async (storeId: string, body: FormData, itemId?: number): Promise<ApiResponse<any>> => {
    try {
        const url = API + 'business/store/' + storeId + '/marketing/events' + (itemId ? '/' + itemId : '');
        const response = await axios.post(url, body, { headers: getHeadersAuth('multipart/form-data') });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

export const setDiscountStatus = async (storeId: string, type: DiscountType, itemId: number, isActive: boolean): Promise<ApiResponse<any>> => {
    try {
        const response = await axios.put(API + 'business/store/' + storeId + '/marketing/' + type + '/' + itemId + '/status', { is_active: isActive }, { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

export const getStoreAds = async (storeId: string): Promise<ApiResponse<Paginated<StoreAd>>> => {
    try {
        const response = await axios.get(API + 'business/store/' + storeId + '/ads?per_page=1000', { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

export const getAdsPricing = async (storeId: string): Promise<ApiResponse<AdsPricing>> => {
    try {
        const response = await axios.get(API + 'business/store/' + storeId + '/ads/pricing', { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

export const getAdsAudience = async (storeId: string, segment: AudienceSegment): Promise<ApiResponse<{ segment: string; count: number }>> => {
    try {
        const response = await axios.get(API + 'business/store/' + storeId + '/ads/audience?segment=' + segment, { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

// Crea el anuncio y cobra por adelantado. FormData: data (JSON AdPayload) e image
export const createStoreAd = async (storeId: string, body: FormData): Promise<ApiResponse<any>> => {
    try {
        const response = await axios.post(API + 'business/store/' + storeId + '/ad', body, { headers: getHeadersAuth('multipart/form-data') });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

// Edita una campaña en curso. FormData: data (JSON AdUpdatePayload) e image opcional
export const updateStoreAd = async (storeId: string, adId: number, body: FormData): Promise<ApiResponse<any>> => {
    try {
        const response = await axios.post(API + 'business/store/' + storeId + '/ad/' + adId, body, { headers: getHeadersAuth('multipart/form-data') });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

// Termina el anuncio y devuelve al saldo lo no consumido
export const finishStoreAd = async (storeId: string, adId: number): Promise<ApiResponse<any>> => {
    try {
        const response = await axios.put(API + 'business/store/' + storeId + '/ad/' + adId + '/finish', {}, { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

export const getToppingsByStore = async (storeId: string): Promise<ApiResponse<StoreTopping[]>> => {
    try {
        const response = await axios.get(API + 'business/store/' + storeId + '/toppings', { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

export const getToppingGroupsByStore = async (storeId: string): Promise<ApiResponse<StoreToppingGroup[]>> => {
    try {
        const response = await axios.get(API + 'business/store/' + storeId + '/toppings-groups', { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

export const getAddonsByStore = async (storeId: string): Promise<GetProductResponse> => {
    try {
        const response = await axios.get(API + 'business/store/' + storeId + "/addons?per_page=1000", { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

export const createAddon = async (storeId: string, body: Object): Promise<GetProductResponse> => {
    try {
        const response = await axios.post(API + 'business/store/' + storeId + "/addon", body, { headers: getHeadersAuth() });
        // 'Content-Type': 'multipart/form-data',
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

export const getAllCategories = async (): Promise<GetAllCategoriesResponse> => {
    try {
        const response = await axios.get(API + 'business/categories?per_page=1000', { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

export const getAllDeliverySchedulesByStore = async (storeId: string): Promise<GetAllSchedulesResponse> => {
    try {
        const response = await axios.get(API + 'business/store/' + storeId + '/schedules', { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

export const updateScheduleByStore = async (storeId: string, schedules: Object): Promise<GetProductResponse> => {
    try {
        const response = await axios.put(API + 'business/store/' + storeId + "/schedule", schedules, { headers: getHeadersAuth() });
        // 'Content-Type': 'multipart/form-data',
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

export const getAllDeliveryScheduleRulesByStore = async (storeId: string): Promise<GetAllSchedulesResponse> => {
    try {
        const response = await axios.get(API + 'business/store/' + storeId + '/schedules-rules', { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}
export const updateRulesByStore = async (storeId: string, rules: Object): Promise<GetProductResponse> => {
    try {
        const response = await axios.put(API + 'business/store/' + storeId + "/schedule-rules", rules, { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

export const getTransactionsByStore = async (storeId: string): Promise<GetTransactionResponse> => {
    try {
        const response = await axios.get(API + 'business/store/' + storeId + '/transactions', { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

export const getReferralDetailsByStore = async (storeId: string): Promise<GetReferralResponse> => {
    try {
        const response = await axios.get(API + 'business/store/' + storeId + '/referrals', { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

export const getDeliveryTypesByStore = async (storeId: string): Promise<GetDeliveryTypeResponse> => {
    try {
        const response = await axios.get(API + 'business/store/' + storeId + '/delivery-types', { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

export const updateDeliveryTypes = async (storeId: string, deliveryTypes: Object): Promise<GetProductResponse> => {
    try {
        const response = await axios.put(API + 'business/store/' + storeId + "/delivery-types", deliveryTypes, { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

export const getAlertSettingsByStore = async (storeId: string): Promise<GetAlertSettingResponse> => {
    try {
        const response = await axios.get(API + 'business/store/' + storeId + '/alert-settings', { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

export const updateAlertSettings = async (storeId: string, settings: Object): Promise<GetProductResponse> => {
    try {
        const response = await axios.put(API + 'business/store/' + storeId + "/alert-settings", settings, { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

export const getLocations = async (): Promise<GetLocationResponse> => {
    try {
        const response = await axios.get(API + 'locations', { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

export const getAllStoresCategories = async (): Promise<GetAllCategoriesResponse> => {
    try {
        const response = await axios.get(API + 'business/stores/categories?per_page=1000', { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

export const createStore = async (storePayload: Object): Promise<PostProductResponse> => {
    try {
        const response = await axios.post(API + 'business/store', storePayload, { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

export const getStoreProfile = async (storeId: string): Promise<StoreProfileResponse> => {
    try {
        const response = await axios.get(API + 'business/store/' + storeId + '/profile', { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

// Actualización parcial: solo se modifican los campos presentes en el body
export const updateStoreProfile = async (storeId: string, body: StoreProfileUpdateBody): Promise<StoreProfileResponse> => {
    try {
        const response = await axios.put(API + 'business/store/' + storeId + '/profile', body, { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

export const getOrderStatuses = async (): Promise<GetOrderStatusesResponse> => {
    try {
        const response = await axios.get(API + 'business/orders/statuses', { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

export const getOrdersByStore = async (storeId: string, params: StoreOrdersParams = {}): Promise<GetStoreOrdersResponse> => {
    try {
        const response = await axios.get(API + 'business/store/' + storeId + '/orders', { headers: getHeadersAuth(), params });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

export const getOrderByStore = async (storeId: string, orderId: number): Promise<GetStoreOrderDetailResponse> => {
    try {
        const response = await axios.get(API + 'business/store/' + storeId + '/order/' + orderId, { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

// La tienda solo puede avanzar la orden al siguiente estado; code es obligatorio al entregar un pickup
export const updateOrderStatusByStore = async (storeId: string, orderId: number, body: { status_id: number; code?: string }): Promise<UpdateOrderStatusResponse> => {
    try {
        const response = await axios.put(API + 'business/store/' + storeId + '/order/' + orderId + '/status', body, { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

export const getStoreDashboard = async (storeId: string, range: DashboardRange): Promise<GetStoreDashboardResponse> => {
    try {
        const response = await axios.get(API + 'business/store/' + storeId + '/dashboard', { headers: getHeadersAuth(), params: { range } });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

/* ------------------------------------------------------------------ */
/*            Flota de empresas de domicilios (central)               */
/* ------------------------------------------------------------------ */

const fleetUrl = (companyId: number | string, path: string) => API + 'business/company/' + companyId + path;

// Endpoints de administración del backend: prefijo admin/v1 (AdministratorMiddleware, roles_id = 1)
const API_ADMIN = API.replace(/v1\/?$/, 'admin/v1/');

// Recorrido real del domiciliario en la entrega; con el token base para que funcione aunque se esté
// navegando como otro usuario
export const getDeliveryRoute = async (deliveryId: number): Promise<ApiResponse<DeliveryRoute>> => {
    try {
        const response = await axios.get(API_ADMIN + 'delivery/' + deliveryId + '/route', { headers: getHeadersAdminAuth() });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

export const getMyDeliveryCompanies = async (): Promise<ApiResponse<DeliveryCompany[]>> => {
    try {
        const response = await axios.get(API + 'business/companies', { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

// Lo que recorrió el domiciliario en su entrega en curso y la ruta que le falta (recortada cerca del cliente)
export const getFleetCourierRoute = async (companyId: number, courierId: number): Promise<ApiResponse<FleetCourierRoute>> => {
    try {
        const response = await axios.get(fleetUrl(companyId, '/courier/' + courierId + '/route'), { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

export const getFleetLive = async (companyId: number): Promise<ApiResponse<FleetLive>> => {
    try {
        const response = await axios.get(fleetUrl(companyId, '/live'), { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

export const getFleetAlerts = async (companyId: number): Promise<ApiResponse<FleetDelivery[]>> => {
    try {
        const response = await axios.get(fleetUrl(companyId, '/alerts'), { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

export const getFleetDeliveries = async (companyId: number, params: { group: 'active' | 'unassigned' | 'history'; search?: string; courier_id?: number; date_from?: string; date_to?: string; page?: number; per_page?: number }): Promise<ApiResponse<Pagination<FleetDelivery>>> => {
    try {
        const response = await axios.get(fleetUrl(companyId, '/deliveries'), { headers: getHeadersAuth(), params });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

export const getFleetDelivery = async (companyId: number, deliveryId: number): Promise<ApiResponse<FleetDeliveryDetail>> => {
    try {
        const response = await axios.get(fleetUrl(companyId, '/delivery/' + deliveryId), { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

export const getAssignmentCandidates = async (companyId: number, deliveryId: number): Promise<ApiResponse<AssignmentCandidate[]>> => {
    try {
        const response = await axios.get(fleetUrl(companyId, '/delivery/' + deliveryId + '/candidates'), { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

// Asignación directa: queda asignado sin esperar a que el domiciliario acepte
export const assignFleetDelivery = async (companyId: number, deliveryId: number, courierId: number): Promise<ApiResponse<{ id: number } | null>> => {
    try {
        const response = await axios.post(fleetUrl(companyId, '/delivery/' + deliveryId + '/assign'), { courier_id: courierId }, { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        return processFleetError(error)
    }
}

export const getFleetCouriers = async (companyId: number): Promise<ApiResponse<FleetCourier[]>> => {
    try {
        const response = await axios.get(fleetUrl(companyId, '/couriers'), { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

export const blockFleetCourier = async (companyId: number, courierId: number, blocked: boolean, reason?: string): Promise<ApiResponse<{ id: number; blocked: boolean } | null>> => {
    try {
        const response = await axios.put(fleetUrl(companyId, '/courier/' + courierId + '/block'), { blocked, reason }, { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        return processFleetError(error)
    }
}

export const requestCourierLink = async (companyId: number, phoneNumber: string): Promise<ApiResponse<{ name: string } | null>> => {
    try {
        const response = await axios.post(fleetUrl(companyId, '/couriers/link/request'), { phone_number: phoneNumber }, { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        return processFleetError(error)
    }
}

export const confirmCourierLink = async (companyId: number, phoneNumber: string, code: string): Promise<ApiResponse<{ courier_id: number } | null>> => {
    try {
        const response = await axios.post(fleetUrl(companyId, '/couriers/link/confirm'), { phone_number: phoneNumber, code }, { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        return processFleetError(error)
    }
}

// Las respuestas 404/409/502 de la flota traen un mensaje para mostrar: se devuelven en vez de lanzarse
function processFleetError(error: any) {
    if (error?.response?.data?.[0] && error.response.status !== 401) {
        return error.response.data[0];
    }
    return processErrorResponse(error);
}

// Sesiones del usuario (una por dispositivo donde inició sesión)
export const getUserSessions = async (): Promise<ApiResponse<UserSession[]>> => {
    try {
        const response = await axios.get(API + 'user/sessions', { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

export const revokeUserSession = async (sessionId: string): Promise<ApiResponse<{ id: string; is_current: boolean } | null>> => {
    try {
        const response = await axios.delete(API + 'user/sessions/' + sessionId, { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

export const revokeOtherUserSessions = async (): Promise<ApiResponse<{ revoked: number } | null>> => {
    try {
        const response = await axios.delete(API + 'user/sessions', { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

function processErrorResponse(error: any) {
    console.log(error);
    // console.log(error.response);
    console.log("Start Status ");
    console.log(error.response.status);
    console.log("End Status ");
    if (error.response && (error.response.status === 400 || error.response.status === 401 || error.response.status === 500)) {
        if (error.response.status === 401) {
            console.log(error);
            // Sesión cerrada desde otro dispositivo: el login muestra el motivo
            const body = error.response.data?.[0];
            if (body?.code_app === 'SESSION_REVOKED') {
                sessionStorage.setItem('logout_reason', body.message);
            }
            userLogout()
        } else {
            return error.response.data[0];
        }
    } else {
        throw error;
    }
}

export const getCurrentUser = async (): Promise<UserGetModel> => {
    try {
        const response = await axios.get(API + 'user/current', { headers: getHeadersAuth() });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}

export const saveDevice = async (body: Object): Promise<ApiResponse<any>> => {
    try {
        const headers = {
            'Content-Type': 'application/json'
        }

        const response = await axios.post(API + 'device', body, { headers });
        return response.data[0];
    } catch (error: any) {
        return processErrorResponse(error)
    }
}
